<template>
  <div class="seo-page">
    <section class="seo-hero">
      <span class="seo-badge">Steam Hours Per Day</span>
      <h1>See How Many Hours You Play on Steam Per Day</h1>
      <p>
        Steam can tell you how many hours you have on a game, but not which days you played them — it keeps a
        lifetime total and a two-week figure, nothing in between. steamplayti.me records your playtime every day,
        so you get hours per day, per month and per game.
      </p>
      <div class="seo-cta-group">
        <router-link class="seo-primary-btn" to="/">See my daily playtime</router-link>
        <a class="seo-secondary-link" href="#daily-views">What you get →</a>
      </div>
      <ul class="seo-highlight-list">
        <li>
          <span class="seo-highlight-icon">📊</span>
          <div>A daily chart of your hours, which you can stack by game.</div>
        </li>
        <li>
          <span class="seo-highlight-icon">⏱️</span>
          <div>Your average hours per day for any range — a week, a month, or the whole year.</div>
        </li>
        <li>
          <span class="seo-highlight-icon">🗓️</span>
          <div>A play calendar, your longest streak, and playtime by month.</div>
        </li>
      </ul>
    </section>

    <SeoDailyPlaytimeInsights />

    <section class="seo-section" id="daily-views">
      <h2>Your Playtime, Day by Day</h2>
      <p>
        Every day steamplayti.me compares your hours on record with the day before. The difference is what you played
        that day, per game — so the daily numbers come from Steam's own counters, not from estimates.
      </p>
      <div class="seo-grid">
        <div class="seo-card">
          <h3>Daily Chart</h3>
          <p>Hours per day over the range you pick, with each game's share visible, plus a trend line for the total.</p>
        </div>
        <div class="seo-card">
          <h3>Average Per Day</h3>
          <p>Choose last week, last 30 days, last 365 days or anything in between, and get your average daily playtime for it.</p>
        </div>
        <div class="seo-card">
          <h3>Calendar &amp; Streaks</h3>
          <p>A play calendar shows which days you played and how much, with your longest daily streak called out.</p>
        </div>
      </div>
    </section>

    <section class="seo-section">
      <h2>Per Month, Too</h2>
      <p>
        Daily numbers roll up into a monthly view, so you can see at a glance whether you played more this month
        than last.
      </p>
      <div class="seo-stats">
        <div class="seo-stat">
          <div class="label">Granularity</div>
          <div class="value">Daily</div>
        </div>
        <div class="seo-stat">
          <div class="label">Rollups</div>
          <div class="value">Week · Month · Year</div>
        </div>
        <div class="seo-stat">
          <div class="label">Cost</div>
          <div class="value">Free</div>
        </div>
      </div>
    </section>

    <section class="seo-section">
      <h2>Steam Hours Per Day FAQ</h2>
      <div class="seo-faq">
        <div class="seo-faq-item">
          <h3>Can Steam show my playtime per day?</h3>
          <p>No. Steam shows hours on record per game and hours played in the past two weeks, but it does not keep a day-by-day record.</p>
        </div>
        <div class="seo-faq-item">
          <h3>Can I see my daily playtime from before I signed up?</h3>
          <p>No — and no tool can, because Steam never stored it. Daily tracking starts from your first day on steamplayti.me; your lifetime total from before that is still counted, just not split by day.</p>
        </div>
        <div class="seo-faq-item">
          <h3>When do my daily charts start?</h3>
          <p>After your first full day of tracking. The first day only sets the starting point; each day after it shows what you played.</p>
        </div>
        <div class="seo-faq-item">
          <h3>Do I need to keep anything running?</h3>
          <p>No. There is nothing to install — your profile is updated automatically every day as long as your Steam game details stay public.</p>
        </div>
      </div>
    </section>
    <SeoRelatedTools current-slug="steam-hours-per-day" />
  </div>
</template>

<script>
import { applyMeta, pageStructuredData } from '../utils/meta';
import SeoRelatedTools from '../components/SeoRelatedTools.vue';
import SeoDailyPlaytimeInsights from '../components/SeoDailyPlaytimeInsights.vue';

const META_DESCRIPTION = 'Steam does not record playtime per day. steamplayti.me does: daily hours per game, your average per day, a play calendar, streaks and playtime by month.';

const META = {
  title: 'Steam Hours Per Day: Daily & Monthly Playtime | steamplayti.me',
  description: META_DESCRIPTION,
  keywords: 'steam hours played per day, steam playtime per day, steam daily playtime, steam playtime by month, steam playtime by day',
  canonical: 'https://steamplayti.me/page/steam-hours-per-day/',
  ogTitle: 'See Your Steam Hours Per Day',
  ogDescription: 'Daily Steam playtime per game, your average per day, and playtime by month.',
  ogUrl: 'https://steamplayti.me/page/steam-hours-per-day/',
  twitterTitle: 'See Your Steam Hours Per Day',
  twitterDescription: 'Steam keeps no daily playtime. steamplayti.me records it every day.',
  structuredData: pageStructuredData({
    name: 'Steam Hours Per Day',
    description: META_DESCRIPTION,
    canonical: 'https://steamplayti.me/page/steam-hours-per-day/',
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Tools & Blog', path: '/blog/' },
      { name: 'Steam Hours Per Day', path: 'https://steamplayti.me/page/steam-hours-per-day/' },
    ],
  }),
};

export default {
  name: 'SteamHoursPerDayPage',
  components: { SeoRelatedTools, SeoDailyPlaytimeInsights },
  mounted() {
    applyMeta(META);
  },
  beforeUnmount() {
    applyMeta();
  },
};
</script>

<style src="../styles/seo-page.css"></style>
